// Aparência dos personagens: o contrato entre o elenco (src/core/cast.ts) e a arte
// (src/web/art/character/*).
//
// Um personagem é um boneco de papel em camadas: corpo + cabelo + roupa + acessório,
// cada um com uma cor tirada de uma rampa. As listas de ids abaixo são FIXAS: a arte
// implementa exatamente estes ids e o elenco só usa estes ids. Ids novos entram aqui
// primeiro.

/** Tipos de corpo (mesma geometria de cabeça/tronco/pernas, silhuetas diferentes). */
export const BODY_IDS = ['padrao', 'esguio', 'robusto'] as const;

/** Cortes de cabelo. 'careca' não desenha nada. */
export const HAIR_IDS = [
  'careca',
  'raspado',
  'curto',
  'topete',
  'lateral',
  'cacheado',
  'crespo-curto',
  'black-power',
  'moicano',
  'franja',
  'chanel',
  'longo-liso',
  'longo-ondulado',
  'rabo-de-cavalo',
  'coque',
  'trancas',
  'maria-chiquinha',
  'dreads',
] as const;

/** Roupas (sobreposição no tronco; a manga é a cor 'shirt' do corpo). */
export const OUTFIT_IDS = [
  'camiseta',
  'gola-v',
  'polo',
  'camisa-social',
  'xadrez',
  'moletom',
  'capuz',
  'jaqueta',
  'blazer',
  'colete',
  'macacao',
  'vestido',
  'regata',
  'jaleco',
] as const;

/** Acessórios (opcionais). */
export const ACCESSORY_IDS = [
  'oculos',
  'oculos-escuros',
  'fone',
  'headset',
  'bone',
  'gorro',
  'barba',
  'bigode',
  'cracha',
  'lenco',
  'brinco',
  'laco',
] as const;

export type BodyId = (typeof BODY_IDS)[number];
export type HairId = (typeof HAIR_IDS)[number];
export type OutfitId = (typeof OUTFIT_IDS)[number];
export type AccessoryId = (typeof ACCESSORY_IDS)[number];

/** Quantas cores existem em cada rampa (ver src/web/art/palette.ts). */
export const SKIN_TONE_COUNT = 8;
export const HAIR_COLOR_COUNT = 10;
export const CLOTH_COLOR_COUNT = 14;

/** A aparência completa de um personagem. Cores são índices nas rampas. */
export interface Look {
  body: BodyId;
  /** 0..SKIN_TONE_COUNT-1 */
  skin: number;
  hair: HairId;
  /** 0..HAIR_COLOR_COUNT-1 */
  hairColor: number;
  outfit: OutfitId;
  /** Cor da roupa (e da manga). 0..CLOTH_COLOR_COUNT-1 */
  shirt: number;
  /** Cor da calça. 0..CLOTH_COLOR_COUNT-1 */
  pants: number;
  /** Cor do sapato. 0..CLOTH_COLOR_COUNT-1 */
  shoes: number;
  accessory?: AccessoryId;
  /** Cor do acessório. 0..CLOTH_COLOR_COUNT-1 */
  accColor?: number;
}

/** Aparência de um id qualquer, por hash (para quem não está no elenco). */
export function lookFromHash(hash: number): Look {
  const pick = <T>(list: readonly T[], salt: number): T => list[Math.floor(hash / salt) % list.length] as T;
  const hasAcc = Math.floor(hash / 7) % 3 === 0;
  return {
    body: pick(BODY_IDS, 1),
    skin: Math.floor(hash / 3) % SKIN_TONE_COUNT,
    hair: pick(HAIR_IDS, 5),
    hairColor: Math.floor(hash / 11) % HAIR_COLOR_COUNT,
    outfit: pick(OUTFIT_IDS, 13),
    shirt: Math.floor(hash / 17) % CLOTH_COLOR_COUNT,
    pants: Math.floor(hash / 19) % CLOTH_COLOR_COUNT,
    shoes: Math.floor(hash / 23) % CLOTH_COLOR_COUNT,
    ...(hasAcc ? { accessory: pick(ACCESSORY_IDS, 29), accColor: Math.floor(hash / 31) % CLOTH_COLOR_COUNT } : {}),
  };
}
